import React, {useState, useEffect} from "react";
import axios from "axios";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {FloatLabel} from "primereact/floatlabel";
import {InputText} from "primereact/inputtext";
import {base, get_contract_instances} from "../../../api";
import DisabledFormFields from "./widgets/DisabledFormFields";
import DocumentTypeSelector from "./widgets/DocumentTypeSelector";
import AutoAddRemoveRows from "./widgets/AutoAddRemoveRows";
import {AutoComplete} from "primereact/autocomplete";

export default function ReportAddDialogForm({
	setIsDialogVisible,
	data,
	selectedTemplatePage,
	fetchContractData,
	isDialogVisible,
	setNewContractTitle,
	newContractTitle,
	toast,
	base,
	projects_url,
}) {
	const [selectedProject, setSelectedProject] = useState(null);
	const [formRelease, setFormRelease] = useState(false);
	const [projects, setProjects] = useState([]);
	const [filteredProjects, setFilteredProjects] = useState([]);
	const [selectedKeys, setSelectedKeys] = useState([]);
	const [selectedID, setSelectedID] = useState();
	const [availableKeys, setAvailableKeys] = useState([]);
	const [inputValues, setInputValues] = useState({});
	const [proccessName, setProccessName] = useState("");
	const [selectedTypes, setSelectedTypes] = useState([]);
	const [cleanedFileNames, setCleanedFileNames] = useState([]);
	const [disabledBtn, setDisabledBtn] = useState(true);
	const [rowsError, setRowsError] = useState(false);
	const [rows, setRows] = useState([
		{description: "", unit: null, price: "", count: "", expense_type: null},
	]);

	useEffect(() => {
		axios
			.get(base + projects_url)
			.then(r => setProjects(r.data))
			.catch(() => {});
	}, []);

	const handleRemoveInput = variable => {
		setSelectedKeys(selectedKeys.filter(item => item.variable !== variable.variable));
		setInputValues(prev => {
			const newValues = {...prev};
			delete newValues[variable.variable];
			return newValues;
		});
	};

	useEffect(() => {
		if (isDialogVisible && data && selectedTemplatePage) {
			const filteredData = data.filter(item => item.contract_type?.id === selectedTemplatePage.id);
			if (filteredData.length > 0) {
				setSelectedID(filteredData[0].id);
			}
			if (selectedTemplatePage.form_fields) {
				const formFields = JSON.parse(selectedTemplatePage.form_fields);
				const initialSelectedKeys = formFields.map(item => ({
					variable: item.variable,
					variable_description: item.variable_description,
				}));
				setSelectedKeys(initialSelectedKeys);
			}
			if (selectedTemplatePage.file_templates?.length > 0) {
				const cleanedFiles = selectedTemplatePage.file_templates
					.map(file => {
						const fileName = file?.file_name?.split("/").pop();
						if (fileName) {
							const prefixMatch = fileName.match(/^[^_]+_/);
							const cleanedFileName = prefixMatch ? fileName.replace(prefixMatch[0], "") : fileName;
							return {...file, cleanedFileName};
						}
						return null;
					})
					.filter(file => file !== null);

				setCleanedFileNames(cleanedFiles);
			}
			const formFields = selectedTemplatePage?.form_fields
				? JSON.parse(selectedTemplatePage.form_fields)
				: [];
			if (Array.isArray(formFields)) {
				const filteredKeys = formFields.filter(
					item => !selectedKeys.some(key => key.variable === item.variable),
				);
				setAvailableKeys(filteredKeys);
			}
		}
	}, [isDialogVisible, data, selectedTemplatePage]);

	useEffect(() => {
		const areAllFieldsFilled = rows.every(
			row => row.description && row.unit && row.price && row.count && row.expense_type,
		);
		const areAllInputFieldsFilled = selectedKeys.every(key => {
			const value = inputValues[key.variable];
			return typeof value === "string"
				? value.trim() !== ""
				: value !== null && value !== undefined;
		});
		if (proccessName && areAllFieldsFilled && selectedTypes.length > 0 && areAllInputFieldsFilled) {
			setDisabledBtn(false);
		} else {
			setDisabledBtn(true);
		}
	}, [proccessName, rows, selectedTypes, inputValues, selectedKeys]);

	const handleInputChange = (variable, value) => {
		setInputValues(prev => ({...prev, [variable]: value}));
	};

	const validateRows = () => {
		return rows.every(row => row.description && row.count && row.price && row.expense_type);
	};

	const saveData = async () => {
		if (!validateRows()) {
			setRowsError(true);
			return;
		}

		const payloadInstance = {
			name: proccessName,
			enabled_template_types: selectedTypes.map(type => type.id),
			contract_type: selectedTemplatePage.id,
			form_fields: selectedKeys,
			inputValues: inputValues,
		};

		const payloadProducts = rows.map(row => ({
			product: row.expense_type?.id || row.expense_type,
			price: row.price,
			unit: row.unit?.id || row.unit,
			count: row.count,
			description: row.description,
		}));

		try {
			const instanceResponse = await axios.post(base + get_contract_instances, payloadInstance, {
				headers: {
					"Content-Type": "application/json",
				},
			});
			const contractId = instanceResponse.data.id;
			const productsResponse = await axios.post(
				`${base + get_contract_instances + contractId}/bulk-upload/`,
				payloadProducts,
				{
					headers: {
						"Content-Type": "application/json",
					},
				},
			);
			setIsDialogVisible(false);
			fetchContractData();
			setRowsError(false);
			clearFormState();
			toast.current.show({
				severity: "success",
				summary: "Success",
				detail: "Data saved successfully",
			});
		} catch (error) {
			console.error("Error saving data:", error);
			toast.current.show({
				severity: "error",
				summary: "Error",
				detail: "Failed to save data",
			});
		}
	};

	const clearFormState = () => {
		setSelectedProject(null);
		setFormRelease(false);
		setSelectedKeys([]);
		setSelectedID(null);
		setAvailableKeys([]);
		setInputValues({});
		setProccessName("");
		setSelectedTypes([]);
		setCleanedFileNames([]);
		setRows([{description: "", unit: null, price: "", count: "", expense_type: null}]);
		setDisabledBtn(true);
	};

	useEffect(() => {
		if (selectedProject) {
			const isValidProject = filteredProjects.some(project => project.id === selectedProject.id);

			if (isValidProject) {
				setFormRelease(true);
			} else {
				setFormRelease(false);
			}
		}
	}, [selectedProject, filteredProjects]);

	const searchProject = event => {
		const q = event.query.toLowerCase();
		setFilteredProjects(
			projects.filter(
				proj =>
					proj.full_name_am.toLowerCase().includes(q) ||
					proj.name.toLowerCase().includes(q) ||
					String(proj.id).toLowerCase().includes(q),
			),
		);
	};

	useEffect(() => {
		const dialogContent = document.querySelector(".p-dialog .p-dialog-content");
		if (dialogContent) {
			dialogContent.scrollTop = 0;
		}
	}, [isDialogVisible]);

	const dialogFooter = formRelease && (
		<div className="pt-3">
			<Button
				label="Cancel"
				icon="pi pi-times"
				severity="secondary"
				rounded
				className="mx-1"
				onClick={() => {
					setIsDialogVisible(false);
					clearFormState();
				}}
			/>
			<Button
				label="Save"
				icon="pi pi-check"
				severity="primary"
				rounded
				className="mx-1"
				disabled={disabledBtn}
				onClick={saveData}
				autoFocus
			/>
		</div>
	);

	return (
		<Dialog
			visible={isDialogVisible}
			onHide={() => {
				setIsDialogVisible(false);
				clearFormState();
			}}
			header={selectedTemplatePage.name + " - " + newContractTitle}
			modal
			footer={dialogFooter}
			className="col-lg-8 col-11 mx-auto">
			<div className="row">
				<div>
					<label htmlFor="project">Select Project</label>
					<AutoComplete
						id="project"
						value={selectedProject}
						suggestions={filteredProjects}
						completeMethod={searchProject}
						field="full_name_am"
						onChange={e => setSelectedProject(e.value)}
						placeholder="Search Project by Name or ID"
						dropdown
						className="w-100"
						panelStyle={{width: "300px"}}
						itemTemplate={item => (
							<div>
								<div className="text-dark">{item.full_name_am}</div>
								<small className="text-primary">
									{item.name || "No code available"} (ID: {item.id})
								</small>
							</div>
						)}
					/>
				</div>

				{formRelease && (
					<>
						<div className="col-12 fs-5 fw-bold opacity-50 border-bottom pb-2 mb-2">Contract</div>

						<div className="col-12">
							<FloatLabel className="">
								<InputText
									className="w-100"
									id="proccessName"
									value={proccessName}
									onChange={e => {
										setProccessName(e.target.value);
										setNewContractTitle(e.target.value);
									}}
								/>
								<label htmlFor="proccessName">Contract Title</label>
							</FloatLabel>
						</div>

						<div className="row">
							<div className="col-12 fs-5 fw-bold opacity-50 border-bottom pb-2 mb-2">
								Contract form
							</div>
							<span className="col-12 mb-3 small fst-italic">
								Note: if there is no available variable please go back and edit the process and
								select the variables
							</span>

							<div className="col-12 mb-3">
								<div className="card b-radius b-shadow p-3 ">
									<div className="row">
										{selectedKeys.length > 0 ? (
											selectedKeys.map(item => (
												<DisabledFormFields
													item={item}
													disabled={false}
													remove={false}
													inputValues={inputValues}
													handleInputChange={handleInputChange}
													handleRemoveInput={handleRemoveInput}
												/>
											))
										) : (
											<div className="col-12">
												<div className="d-flex justify-content-center align-items-center p-5 opacity-50">
													No fields selected
												</div>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
						<AutoAddRemoveRows rows={rows} rowsError={rowsError} setRows={setRows} />
						<div>
							<DocumentTypeSelector
								setSelectedTypes={setSelectedTypes}
								cleanedFileNames={cleanedFileNames}
							/>
						</div>
					</>
				)}
			</div>
		</Dialog>
	);
}
